import { ref } from 'vue'
import cookie from '@/common/utils/cookie'
import constant from '@/common/resources/constant'
import { EventBus } from '@/common/utils/event-bus'
import { getConsentRenewInfo } from '@/common/requests/data-fetch/get-consent-renew-info'
import { postConsentRenewAgree } from '@/common/requests/data-fetch/post-consent-renew-agree'
import { postConsentRenewDisagree } from '@/common/requests/data-fetch/post-consent-renew-disagree'
import { postConsentRenewEmail } from '@/common/requests/data-fetch/post-consent-renew-email'
import { CONSENT_PROMOTION_LAYER_POPUP } from '@/common/resources/const'
import {
  ACTION_TYPE_NONE,
  ACTION_TYPE_SHOW_POPUP,
  TOAST_MSG_CONSENT_AGREED,
  TOAST_MSG_CONSENT_AGREE_ERROR,
  TOAST_MSG_NETWORK_ERROR,
  SS_KEY_SHOULD_CHECK_CONSENT,
  COOKIE_KEY_IS_CONSENT_POPUP_HIDDEN,
  TOAST_MSG_CONSENT_POPUP_HIDDEN,
  ERROR_CODE_UNAUTHORIZED,
  COOKIE_EXPIRY_DAYS,
  TOAST_MSG_ALREADY_AGREED,
  TOAST_MSG_CONSENT_DISAGREED,
  ERROR_CODE_DUPLICATE_REQUEST,
} from '@/common/utils/consent/const'

export const useConsentRenew = () => {
  const isShowConsentRenewPopup = ref(false)
  const isPopupHidden = ref(false)
  const isPromotionTarget = ref(false)

  const storage = {
    // on login
    setSessionConsentCheck: (value = 'true') => {
      sessionStorage.setItem(SS_KEY_SHOULD_CHECK_CONSENT, `${value}`)
    },

    // on logout
    removeSessionConsentCheck: () => {
      sessionStorage.removeItem(SS_KEY_SHOULD_CHECK_CONSENT)
    },
    getSessionConsentCheck: () => {
      return sessionStorage.getItem(SS_KEY_SHOULD_CHECK_CONSENT)
    },
    setPopupHidden: () => {
      const expires = new Date()
      expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS)
      cookie.setItem(
        COOKIE_KEY_IS_CONSENT_POPUP_HIDDEN,
        'true',
        expires.toUTCString(),
        constant.URL_COOKIE_DOMAIN,
        '/',
      )
    },
  }

  const ui = {
    showLayerPopup: (data) => {
      EventBus.$emit(CONSENT_PROMOTION_LAYER_POPUP, {
        isOpen: true,
        info: data?.info,
      })
    },
    closeLayerPopup: () => {
      EventBus.$emit(CONSENT_PROMOTION_LAYER_POPUP, { isOpen: false })
    },
    showToast: (message = '') => {
      EventBus.$emit('OPEN_PPV_TOAST', () => {}, message)
    },
  }

  /**
   * 가격 변경 동의 팝업 Hidden
   */
  const handleConsentPopupHidden = () => {
    storage.setPopupHidden()
    ui.showToast(TOAST_MSG_CONSENT_POPUP_HIDDEN)
    ui.closeLayerPopup()
  }

  /**
   * email 동의 / 비동의 체크 api 호출
   * 성공시 onSuccess 반환
   * 기동의 or 유효하지 않은 토큰 onInvalid 반환
   * 실패시 onError 반환
   * @param token
   * @param isAgree
   * @param onSuccess
   * @param onInvalid
   * @param onError
   * @returns {Promise<*>}
   */
  const getEmailResult = async ({
    token,
    isAgree,
    onSuccess,
    onInvalid,
    onError,
  }) => {
    const { error, data } = await postConsentRenewEmail({ token, isAgree })

    if (error) {
      const errorCode = error?.data?.code ?? ''
      errorCode === ERROR_CODE_DUPLICATE_REQUEST ||
      errorCode === ERROR_CODE_UNAUTHORIZED
        ? onInvalid()
        : onError()
    } else {
      onSuccess(data)
    }
  }

  /**
   * 가격 변경 동의 내용 노출 대상 여부 확인
   * 대상이면 onSuccess, 대상이 아니면 onInvalid 호출
   * @param onSuccess
   * @param onInvalid
   * @param isTargetType
   * @returns {Promise<undefined|*>}
   */
  const getTargetFromActionType = async ({
    isTargetType,
    onSuccess,
    onInvalid,
  }) => {
    const { data } = await getConsentRenewInfo()
    if (!data) return onInvalid()

    const actionType = data?.actionType ?? ACTION_TYPE_NONE

    return isTargetType(actionType) ? onSuccess(data) : onInvalid(data)
  }

  /**
   * 에러 처리 - 에러 토스트 메세지 출력 후 에러 객체 및 바로 닫기 여부 리턴
   * @param error
   * @returns {{error, isCloseImmediate: boolean}}
   */
  const handleError = (error) => {
    const isUnauthorizedError = error?.data?.code === ERROR_CODE_UNAUTHORIZED
    ui.showToast(
      isUnauthorizedError
        ? TOAST_MSG_CONSENT_AGREE_ERROR
        : TOAST_MSG_NETWORK_ERROR,
    )
    return { error, isCloseImmediate: isUnauthorizedError }
  }

  /**
   * 가격 변동 미동의 버튼 클릭
   * useId 를 받아 api 호출 후 성공시 onSuccess, 실패시 onError
   * data.isAlreadyAgree, data.isAlreadyDisagree 가 true 인 경우 실패를 리턴
   * @param onSuccess
   * @param onError
   * @param userId
   * @returns {Promise<*>}
   */
  const handleRenewDisagree = async ({ onSuccess, onError, userId = '' }) => {
    const { error, data } = await postConsentRenewDisagree(userId)

    if (error) {
      const { isCloseImmediately } = handleError(error)
      return onError(isCloseImmediately)
    }

    if (data) {
      const isInvalid = data?.isAlreadyAgree || data?.isAlreadyDisagree

      if (isInvalid) {
        ui.showToast(TOAST_MSG_CONSENT_AGREE_ERROR)
        return onError(true)
      }

      ui.showToast(TOAST_MSG_CONSENT_DISAGREED)
    }
    return onSuccess()
  }

  /**
   * 가격 변경 동의 버튼 클릭
   * useId 를 받아 api 호출 후 성공시 onSuccess 호출
   * 실패 시 onError 호출
   * @param onSuccess
   * @param onError
   * @param userId
   * @returns {Promise<undefined|*>}
   */
  const handleRenewAgree = async ({ onSuccess, onError, userId = '' }) => {
    const { error, data } = await postConsentRenewAgree(userId)

    if (error) {
      const { isCloseImmediately } = handleError(error)
      return onError(isCloseImmediately)
    }

    if (data) {
      const isAlreadyAgreed = data?.isAlreadyAgree
      storage.setSessionConsentCheck('false')
      ui.showToast(
        isAlreadyAgreed ? TOAST_MSG_ALREADY_AGREED : TOAST_MSG_CONSENT_AGREED,
      )
      return onSuccess()
    }
  }

  /**
   * home 에서 Popup 을 띄울것인지 체크
   * @returns {Promise<void>}
   */

  const checkConsentPromotion = async () => {
    isPopupHidden.value =
      cookie.getItem(COOKIE_KEY_IS_CONSENT_POPUP_HIDDEN) === 'true'
    isPromotionTarget.value = storage.getSessionConsentCheck() === 'true'

    if (isPromotionTarget.value && !isPopupHidden.value) {
      await getTargetFromActionType({
        isTargetType: (actionType) => actionType === ACTION_TYPE_SHOW_POPUP,
        onSuccess: (data) => ui.showLayerPopup(data),
        onInvalid: () => storage.setSessionConsentCheck('false'),
      })
    }
  }

  return {
    storage,
    ui,

    isShowConsentRenewPopup,
    isPopupHidden,
    isPromotionTarget,

    handleConsentPopupHidden,
    handleRenewAgree,
    handleRenewDisagree,
    checkConsentPromotion,
    getTargetFromActionType,
    getEmailResult,
  }
}
